import React, { ReactNode } from "react";

import MuiList from "@mui/material/List";
import Typography from "@mui/material/Typography";

import MobileCard from "../MobileCard";
import MobileCardSkeleton from "../MobileCard/MobileCardSkeleton";

import ActionMenu from "components/ActionMenu";
import { MenuItem } from "components/ActionMenu/types";

import { ReactComponent as ActionsIcon } from "assets/images/actionMenu/actions-menu-icon.svg";

import { DataTableColumn } from "../types";

interface MobileListProps<T extends { id: string }> {
  rows: T[];
  columns: DataTableColumn<T>[];
  actions?: MenuItem<T>[];
  loading?: boolean;
  mobileRenderer?: (row: T) => ReactNode;
  skeletonRowCount?: number;
}

const renderMobileColumn = <T extends { id: string }>(
  row: T,
  col: DataTableColumn<T>,
  isLast: boolean,
) => {
  if (col.renderCell) {
    return <React.Fragment key={col.field}>{col.renderCell(row)}</React.Fragment>;
  }

  const { field, valueFormatter, color, secure } = col;
  const raw = row[field];
  const display = valueFormatter ? valueFormatter(raw) : String(raw ?? "");

  if (!display) return null;

  return (
    <Typography
      key={field}
      variant="body2"
      color={color}
      sx={{ mb: isLast ? 0 : 1 }}
      data-posthog-mask={secure ? "true" : undefined}
    >
      {display}
    </Typography>
  );
};

const renderCard = <T extends { id: string }>(
  row: T,
  columns: DataTableColumn<T>[],
  actions?: MenuItem<T>[],
) => {
  const visibleColumns = columns.filter((col) => !col.hideOnMobile);
  const hasActions = !!actions && actions.length > 0;

  return (
    <MobileCard key={row.id}>
      {hasActions && (
        <MobileCard.Header
          actions={
            <ActionMenu
              mainIcon={<ActionsIcon />}
              menuId={`mobile-action-menu-${row.id}`}
              actions={actions}
              row={row}
            />
          }
        >
          <span />
        </MobileCard.Header>
      )}
      <MobileCard.Body>
        {visibleColumns.map((col, index) =>
          renderMobileColumn(row, col, index === visibleColumns.length - 1),
        )}
      </MobileCard.Body>
    </MobileCard>
  );
};

const MobileList = <T extends { id: string }>(props: MobileListProps<T>) => {
  const {
    rows,
    columns,
    actions,
    loading = false,
    mobileRenderer,
    skeletonRowCount,
  } = props;

  if (loading) {
    return (
      <MuiList sx={{ width: "100%" }}>
        <MobileCardSkeleton
          rowCount={skeletonRowCount}
          withActions={!!actions && actions.length > 0}
        />
      </MuiList>
    );
  }

  return (
    <MuiList sx={{ width: "100%" }}>
      {rows.map((row) =>
        mobileRenderer ? (
          <React.Fragment key={row.id}>{mobileRenderer(row)}</React.Fragment>
        ) : (
          renderCard(row, columns, actions)
        ),
      )}
    </MuiList>
  );
};

export default MobileList;
